import "./zod-csp";
import "@fontsource-variable/onest";
import "@fontsource-variable/unbounded";
import { lazy, StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, Navigate, RouterProvider, useLocation } from "react-router-dom";
import { App } from "./App";
import { hasSectionAccess, shortPathTarget } from "@vekha/contracts";
import { LoadingScreen, UnavailableScreen } from "./components/ui";
import { RouteErrorScreen } from "./components/route-error";
import { NewBuildWatch } from "./components/new-build";
import { AppProvider, readDialogsPart } from "./lib/app-context";
import { AuthProvider, useAuth } from "./lib/auth-context";
import { publicTeamAccessKind } from "./lib/team-access";
import { NativeCalendarProvider, useNativeCalendar } from "./lib/native-calendar-context";
import { ForgotPasswordPage, LoginPage, SetupExpiredPage, SetupPage, TeamAccessCodePage } from "./pages/auth-pages";
import { RecoveryPage } from "./pages/recovery-page";
const AgentConnectPage = lazy(async () => ({ default: (await import("./pages/agent-connect-page")).AgentConnectPage }));
import { ApiRequestError, fetchAuthenticationState, fetchBootstrap } from "./lib/api";
import { partsOfAddress, prefetchParts } from "./lib/analytics-api";
import { fetchCalendarBootstrap } from "./lib/native-calendar-api";
import { NATIVE_CALENDAR_UI_ENABLED } from "./lib/native-calendar-context";
import { startRead } from "./lib/startup-reads";
import { pageSnapshotNeed } from "./lib/page-snapshot";
import { loadAnalyticsPage } from "./lib/analytics-page-loader";
import { loadDialogsPage } from "./lib/dialogs-page-loader";
import { markTelegramLinkPending, startTelegram, telegramInitData, telegramWebApp } from "./lib/telegram";
import { signInWithTelegram } from "./lib/telegram-api";
import { useTelegramBackButton } from "./lib/use-telegram-back-button";
import { APP_STARTED, forgetDialogsContext, startDialogsListReads } from "./lib/dialogs-list-reads";
import "./styles.css";
import "./pages/calendar.css";

// The first reads of the page start together, before React mounts the providers that wait for them one after another
// (17.10.2026: three round trips in a row). On «Аналитика» its code and its numbers start too: they do not wait for the
// bootstrap, the session cookie is enough (an answer refused without a session is simply asked again by the page).
// The page's first script (boot.ts) asks this start's reads early unless the app's code has already started.
Reflect.set(window, APP_STARTED, true);
// A short address (`/m` — «Маркетинг», the menu button of Arkadiy's bot): the screen's own address before anything reads it; the query and
// the hash (Telegram's launch data) stay.
const short = shortPathTarget(window.location.pathname);
if (short) window.history.replaceState(window.history.state, "", `${short}${window.location.search}${window.location.hash}`);
// Inside Telegram (Mini App): the class, the screen of a Direct Link's start parameter, Telegram's script.
startTelegram();
const telegram = telegramInitData();
const path = window.location.pathname;
const analytics = /^\/analytics(?:\/|$)/u.test(path);
// In Telegram without a session the launch data signs in at once, inside the first check: the page opens signed in,
// without a login screen and without a reload. A Telegram id not linked yet gets the ordinary login once.
const authentication = fetchAuthenticationState().then(async (state) => {
  if (!telegram || state.setup.required || state.session !== null) return state;
  try {
    return { ...state, session: await signInWithTelegram(telegram) };
  } catch (caught) {
    markTelegramLinkPending(caught instanceof ApiRequestError && caught.code === "TELEGRAM_NOT_LINKED");
    return state;
  }
});
startRead("auth", () => authentication);
authentication.then((state) => { if (state.session === null) forgetDialogsContext(); }, () => undefined);
// Every page, not only «Аналитика» (17.10.2026 evening: «Диалоги», «Тревоги», «Рассылки» asked auth, then the calendar,
// then the bootstrap — 0.5 s of round trips before the 3 s snapshot even started). Outside «Аналитика» they wait for the
// session to be confirmed (one round trip), so the login page asks nothing it would be refused. The calendar's own page
// reads a bootstrap of its own (scope=calendar), taken only by a calendar load; «Диалоги» need their part of the snapshot
// (without chats: the page asks for the ones it shows), so it starts too, and so does the page's code.
// «Диалоги» in a browser that opened them signed in before (18.10.2026: the list waited for the session check, then the
// snapshot's part — two round trips — and only then was asked): the list's reads start now with the context resolved
// last time (dialogs-list-reads.ts), and the snapshot's part and the bootstrap do not wait for the session check either.
const dialogs = pageSnapshotNeed(path) === "dialogs-part";
// In Telegram the session may only come from the launch data: nothing is read before the check has answered.
const dialogsKnown = dialogs && !telegram && startDialogsListReads(window.location.search);
const signedIn = (analytics && !telegram) || dialogsKnown ? Promise.resolve(true) : authentication.then((state) => state.session !== null, () => false);
const whenSignedIn = <T,>(read: () => Promise<T>) => () => signedIn.then((yes) => yes ? read() : Promise.reject(new Error("no session")));
if (NATIVE_CALENDAR_UI_ENABLED) startRead("calendar", whenSignedIn(fetchCalendarBootstrap));
if (/^\/calendar\/*$/u.test(path)) startRead("calendar-scope-bootstrap", whenSignedIn(() => fetchBootstrap("calendar")));
else startRead("bootstrap", whenSignedIn(() => fetchBootstrap()));
if (dialogs) {
  startRead("dialogs-workspace", whenSignedIn(() => readDialogsPart([])));
  void loadDialogsPage();
}
if (analytics) {
  void loadAnalyticsPage();
  const parts = partsOfAddress(window.location.pathname, window.location.search);
  if (parts && !telegram) prefetchParts(parts);
}

const root = document.getElementById("root");
if (!root) throw new Error("Root element not found");

function Root() {
  const { error, mode, retryAuthentication, session } = useAuth();
  const location = useLocation();
  useTelegramBackButton();
  const publicAccessKind = publicTeamAccessKind(location.pathname);
  if (location.pathname === "/forgot-password" && (mode === "login" || mode === "authenticated")) return <ForgotPasswordPage />;
  switch (mode) {
    case "checking":
      return <LoadingScreen />;
    case "unavailable":
      return (
        <UnavailableScreen
          title="Сервис входа недоступен"
          error={error ?? "Сервер не подтвердил состояние установки и входа."}
          guard="Состояние сессии не подтверждено. Рабочая база не загружена, отправка и изменение настроек недоступны."
          retryLabel="Повторить проверку входа"
          onRetry={retryAuthentication}
        />
      );
    case "setup-expired":
      return <SetupExpiredPage />;
    case "setup":
      return <SetupPage />;
    case "login":
      return publicAccessKind ? <TeamAccessCodePage kind={publicAccessKind} /> : <LoginPage />;
    case "authenticated":
      return publicAccessKind ? <TeamAccessCodePage kind={publicAccessKind} />
        : /^\/recovery\/*$/u.test(location.pathname) ? <RecoveryPage />
        // An AI agent's sign-in (OAuth): the consent screen, without the menu.
        : /^\/agent-connect\/*$/u.test(location.pathname) ? <Suspense fallback={<LoadingScreen />}><AgentConnectPage /></Suspense> : (
        <NativeCalendarProvider key={session?.actor.userId}>
          <AuthenticatedApplication />
        </NativeCalendarProvider>
      );
  }
}

function AuthenticatedApplication() {
  const { session } = useAuth();
  const location = useLocation();
  const calendar = useNativeCalendar();
  const calendarOnly = calendar.enabled && !calendar.bootstrap.operatorAccess;
  const calendarRoute = calendar.enabled && /^\/calendar\/*$/u.test(location.pathname);
  const policy = session?.actor.sectionAccess;
  if (!policy && calendarOnly && !calendarRoute && !(calendar.bootstrap.profile.role === "leader" && location.pathname === "/access")) return <Navigate to="/calendar" replace />;
  const workspaceEnabled = policy ? ["dialogs", "agents", "scenarios", "connections", "status", "action_log", "playground"].some(section => policy.grants.some(grant => grant.section === section)) : !calendarOnly && !calendarRoute;
  return <AppProvider key={workspaceEnabled ? "operator" : "calendar"} workspaceEnabled={workspaceEnabled} pathname={location.pathname} liveDialogs={workspaceEnabled && !/^\/semantic-staging\/*$/u.test(location.pathname)}><App /></AppProvider>;
}

const router = createBrowserRouter([{
  path: "*",
  errorElement: <RouteErrorScreen />,
  element: (
    <AuthProvider>
      <Root />
      <NewBuildWatch />
    </AuthProvider>
  ),
}]);

const render = () => createRoot(root).render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>,
);
// Telegram's script reads its launch parameters from the address hash, which the router's first redirect drops: in
// Telegram the app renders once the script has run (or after 2.5 s without it).
if (telegram) void Promise.race([telegramWebApp(), new Promise((resolve) => setTimeout(resolve, 2_500))]).then(render);
else render();
